import { useBoolean, useTimeout } from "ahooks";
import classNames from "classnames";
import { useCallback, useRef } from "react";
import styles from "./index.module.less";

export interface ExpandableButtonProps {
  /** 展开时显示的内容（文字部分） */
  expandContent?: React.ReactNode;
  /** 始终显示的内容（图标部分） */
  children: React.ReactNode;
  /** 是否自动展开 */
  autoExpand?: boolean;
  /** 自动展开延迟（ms） */
  autoExpandDelay?: number;
  /** 自动收起延迟（ms） */
  autoCollapseDelay?: number;
  /** 自定义类名 */
  className?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 外部控制展开状态 */
  expanded?: boolean;
  /** 展开状态变化回调 */
  onExpandChange?: (expanded: boolean) => void;
}

/**
 * 可展开按钮组件
 * 支持自动展开/收起，hover 展开
 */
export default function ExpandableButton({
  expandContent,
  children,
  autoExpand = true,
  autoExpandDelay = 1000,
  autoCollapseDelay = 2000,
  className,
  style,
  expanded: controlledExpanded,
  onExpandChange,
}: ExpandableButtonProps) {
  const [isExpanded, { setTrue: expand, setFalse: collapse }] =
    useBoolean(false);
  const [isHovered, { setTrue: setHovered, setFalse: setUnhovered }] =
    useBoolean(false);

  const hasAutoExpanded = useRef(false);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 清除收起定时器
  const clearCollapseTimer = useCallback(() => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
  }, []);

  // 自动展开
  useTimeout(
    () => {
      if (hasAutoExpanded.current) return;
      hasAutoExpanded.current = true;
      expand();
      onExpandChange?.(true);

      // 设置自动收起
      collapseTimerRef.current = setTimeout(() => {
        collapse();
        onExpandChange?.(false);
      }, autoCollapseDelay);
    },
    autoExpand && !hasAutoExpanded.current ? autoExpandDelay : undefined
  );

  // hover 进入
  const handleMouseEnter = useCallback(() => {
    setHovered();
    clearCollapseTimer();
  }, [setHovered, clearCollapseTimer]);

  // hover 离开
  const handleMouseLeave = useCallback(() => {
    setUnhovered();
    if (hasAutoExpanded.current) {
      collapse();
      onExpandChange?.(false);
    }
  }, [setUnhovered, collapse, onExpandChange]);

  // 是否展开（支持受控模式）
  const showExpanded =
    controlledExpanded !== undefined
      ? controlledExpanded
      : isExpanded || isHovered;

  return (
    <div
      className={classNames(
        styles.expandableButton,
        showExpanded && styles.expanded,
        className
      )}
      style={style}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.content}>
        {expandContent && (
          <div className={styles.expandWrapper}>
            <div className={styles.expandInner}>{expandContent}</div>
          </div>
        )}
        <div className={styles.mainContent}>{children}</div>
      </div>
    </div>
  );
}
